import { useState, useEffect } from 'react'
import { adminApi } from '../services/api'
import {
  FileText,
  Save,
  Loader2,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  Shield,
  Cookie,
  ScrollText,
  FileCheck
} from 'lucide-react'

export default function LegalPage() {
  const [legal, setLegal] = useState({
    termsUrl: '',
    privacyUrl: '',
    cookieUrl: '',
    dpaUrl: '',
    version: '1.0',
    lastUpdated: null
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await adminApi.getSettings()
      setLegal(response.data.data.settings.legal)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      await adminApi.updateLegalSettings(legal)
      setSuccess('Documenti legali salvati con successo')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  const documents = [
    {
      key: 'termsUrl',
      icon: ScrollText,
      title: 'Termini di Servizio',
      description: 'Condizioni generali di utilizzo del servizio',
      placeholder: 'https://www.iubenda.com/terms-and-conditions/...',
      required: true
    },
    {
      key: 'privacyUrl',
      icon: Shield,
      title: 'Privacy Policy',
      description: 'Informativa sul trattamento dei dati personali',
      placeholder: 'https://www.iubenda.com/privacy-policy/...',
      required: true
    },
    {
      key: 'cookieUrl',
      icon: Cookie,
      title: 'Cookie Policy',
      description: 'Informativa sui cookie utilizzati',
      placeholder: 'https://www.iubenda.com/privacy-policy/.../cookie-policy',
      required: false
    },
    {
      key: 'dpaUrl',
      icon: FileCheck,
      title: 'DPA (Data Processing Agreement)',
      description: 'Accordo per il trattamento dati per conto terzi (GDPR Art. 28)',
      placeholder: 'https://www.iubenda.com/dpa/...',
      required: true
    }
  ]

  const isComplete = legal.termsUrl && legal.privacyUrl && legal.dpaUrl

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Documenti Legali</h1>
          <p className="text-gray-500">Link ai documenti legali generati con Iubenda o simili</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Salva
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <p className="text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {/* Status */}
      <div className={`rounded-lg p-4 ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
        <div className="flex items-center">
          {isComplete ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
              <div>
                <p className="font-medium text-green-800">Documenti completi</p>
                <p className="text-sm text-green-600">Tutti i documenti obbligatori sono configurati</p>
              </div>
            </>
          ) : (
            <>
              <AlertCircle className="h-5 w-5 text-yellow-500 mr-3" />
              <div>
                <p className="font-medium text-yellow-800">Documenti incompleti</p>
                <p className="text-sm text-yellow-600">Configura i documenti legali prima del lancio commerciale</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Info Iubenda */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">📝 Come ottenere i documenti legali</h3>
        <ol className="text-sm text-blue-700 list-decimal list-inside space-y-1">
          <li>Registrati su <a href="https://www.iubenda.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Iubenda.com</a> (o servizio simile)</li>
          <li>Genera i documenti selezionando le caratteristiche del tuo servizio</li>
          <li>Copia gli URL dei documenti generati e incollali qui sotto</li>
          <li>Costo stimato: €100-200/anno per tutti i documenti</li>
        </ol>
      </div>

      {/* Documents */}
      <div className="space-y-4">
        {documents.map((doc) => (
          <div key={doc.key} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg mr-3">
                  <doc.icon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {doc.title}
                    {doc.required && <span className="text-red-500 ml-1">*</span>}
                  </h3>
                  <p className="text-sm text-gray-500">{doc.description}</p>
                </div>
              </div>
              {legal[doc.key] && (
                <a
                  href={legal[doc.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-sm text-primary-600 hover:text-primary-700"
                >
                  Apri
                  <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              )}
            </div>

            <input
              type="url"
              value={legal[doc.key] || ''}
              onChange={(e) => setLegal({ ...legal, [doc.key]: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder={doc.placeholder}
            />

            {!legal[doc.key] && doc.required && (
              <p className="mt-2 text-sm text-yellow-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Documento obbligatorio non configurato
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Version */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Versione Documenti</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Versione
            </label>
            <input
              type="text"
              value={legal.version || ''}
              onChange={(e) => setLegal({ ...legal, version: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="1.0"
            />
            <p className="mt-1 text-xs text-gray-500">
              Incrementa quando aggiorni i documenti (es. 1.0 → 1.1)
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ultimo Aggiornamento
            </label>
            <input
              type="text"
              value={legal.lastUpdated ? new Date(legal.lastUpdated).toLocaleDateString('it-IT') : 'Mai'}
              disabled
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Checklist Pre-Lancio */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-4">✅ Checklist Documenti Legali</h3>
        <div className="space-y-3">
          <CheckItem 
            checked={!!legal.termsUrl} 
            label="Termini di Servizio configurati" 
          />
          <CheckItem 
            checked={!!legal.privacyUrl} 
            label="Privacy Policy configurata" 
          />
          <CheckItem 
            checked={!!legal.dpaUrl} 
            label="DPA (Data Processing Agreement) configurato" 
          />
          <CheckItem 
            checked={!!legal.cookieUrl} 
            label="Cookie Policy configurata (opzionale)" 
            optional 
          />
        </div>
      </div>
    </div>
  )
}

function CheckItem({ checked, label, optional }) {
  return (
    <div className="flex items-center">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center mr-3 ${
        checked 
          ? 'bg-green-100 text-green-600' 
          : optional 
            ? 'bg-gray-100 text-gray-400'
            : 'bg-yellow-100 text-yellow-600'
      }`}>
        {checked ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <div className="w-2 h-2 rounded-full bg-current" />
        )}
      </div>
      <span className={`text-sm ${checked ? 'text-gray-900' : 'text-gray-500'}`}>
        {label}
        {optional && <span className="text-gray-400 ml-1">(opzionale)</span>}
      </span>
    </div>
  )
}
